<template>
  <div
    v-if="stepData"
    class="step"
  >
    <component
      :is="question.questionComponent"
      v-for="question in questions"
      :key="question.questionId || question.questionComponent"
      product-name="car"
      step-id="vehicle"
      :question-id="question.questionId"
      v-bind="question.params || {}"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="stepData"
      :step-shared-data="stepSharedData"
      @toast="showToast($event)"
      @input="stepData = { ...stepData, ...$event }"
      @reset-observer="$emit('reset-observer')"
      @set-step-shared-data="setStepSharedData"
    />
  </div>
</template>
<script>
import { mapActions } from 'vuex';
import {
  inject,
} from '@vue/composition-api';
import {
  createAbtastyScript,
  removeAbtastyScript,
} from '@/helpers/common';

import { cloneDeep } from 'lodash';
import useExperimentMinimiseHelptext from '@/composables/useExperimentMinimiseHelptext';

export default {
  name: 'form-car-step-1',
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
  },
  inject: [
    'constants',
    'config',
  ],
  data () {
    return {
      stepData: null,
      stepSharedData: {},
    };
  },
  watch: {
    stepData: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
    'formObserver.valid': {
      immediate: true,
      handler (val) {
        if (val) this.$emit('valid');
      },
    },
  },
  created () {
    this.$emit('tracking', 'vehicle');
    this.stepData = cloneDeep(this.form.vehicle);
    this.formObserver.reset();

    createAbtastyScript(this.config);
  },
  beforeDestroy () {
    removeAbtastyScript(this.config);
  },
  computed: {
    // [SU-5906] - don't show price sensitive block for Apricot
    dashcamHelpText () {
      return !this.constants.PARTNERS.Apricot.includes(this.config.partner) && {
        value: 'Our insurance providers may give you a discount if you have a dashcam fitted.',
        priceSensitive: true,
      };
    },
    vehicleRegistrationHelpText () {
      const vehicleRegistrationHelpTextR1 = {
        value: `
          <p>
            Enter your registration number to get an accurate quote. If it's not recognised or you don't have it yet, you can still manually enter your vehicle details. You'll need the registration number to finalise your insurance.
          </p>
          <p>
            For new vehicles released this year, the registration number might not be recognised yet, but you can quickly input the details manually.
          </p>
        `,
        collapsible: true,
        openerText: 'Why enter the registration number?',
      };

      return this.isCarHelpTextR1visible ? vehicleRegistrationHelpTextR1 : null;
    },
    registeredKeeperHelpText () {
      const registeredKeeperHelpTextR1 = {
        value: `
          <p>
            The owner legally owns the vehicle, either through purchase or as a gift. The registered keeper, listed on the V5C logbook, is responsible for managing the vehicle's insurance and tax. While often the same, the registered keeper and owner can be different.
          </p>
          <p>
            In a lease or finance agreement, the leasing company is the owner, while you are the registered keeper responsible for the vehicle's day-to-day management.
          </p>
        `,
        collapsible: true,
        openerText: 'Tell me more about the owner and registered keeper',
      };

      return this.isCarHelpTextR1visible ? registeredKeeperHelpTextR1 : null;
    },
    estimatedValueHelpText () {
      const estimatedValueHelpText = 'If the vehicle is written off/stolen, the insurer will usually pay out the market value. <br> Therefore this value is not necessarily the amount you\'ll receive in the event of a claim.';
      const estimatedValueHelpTextTitle = '<b>Good to know</b> <br>';
      const estimatedValuePrefilledHelpText = `
          <p>
            This is an estimated market value for your vehicle, from an independent vehicle valuation provider. It depends on things like mileage, optional extras and overall condition.
          </p>
          <p>
            If the vehicle is written off or stolen, the insurer will usually pay out the market value. This value is not necessarily the amount you'll receive in the event of a claim.
          </p>
          <p>
            If you don't agree with the valuation you can still enter what you think your vehicle is worth.
          </p>
        `;
      const estimatedValuePrefilledHelpTextTitle = '<b>Good to know</b>';

      // eslint-disable-next-line no-extra-boolean-cast
      if (!!this.stepSharedData.estimatedValue) {
        return {
          value: this.isExperimentMinimiseHelptext ? estimatedValuePrefilledHelpText : estimatedValuePrefilledHelpTextTitle + estimatedValuePrefilledHelpText,
          collapsible: this.isExperimentMinimiseHelptext,
          openerText: 'How is this value calculated?',
        };
      }
      return this.isExperimentMinimiseHelptext ? estimatedValueHelpText : estimatedValueHelpTextTitle + estimatedValueHelpText;
    },
    questions () {
      return [
        {
          questionComponent: 'question-vehicle',
          questionId: 'vehicle',
          params: {
            helpText: this.vehicleRegistrationHelpText,
          },
        },
        {
          questionComponent: 'question-about',
          questionId: 'about',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: [
              'vehicleSide',
              'vehicleGreyOrImport',
            ],
            questions: [
              {
                questionId: 'vehicle-side',
                questionComponent: 'question-vehicle-side',
                params: {
                  customFields: {
                    field: 'vehicleSide',
                  },
                  title: 'Is your car right or left hand drive?',
                },
              },
              {
                questionId: 'seats-of-vehicle',
                questionComponent: 'question-base-dropdown',
                params: {
                  customFields: {
                    field: 'seatsOfVehicle',
                  },
                  subtitle: 'How many seats does the car have?',
                  helpText: {
                    value: 'If you are unsure, count how many seatbelts the car has.',
                  },
                  optionsConfig: {
                    placeholder: 'Please select',
                    options: {
                      1: '1',
                      2: '2',
                      3: '3',
                      4: '4',
                      5: '5',
                      6: '6',
                      7: '7',
                      8: '8',
                      9: '9',
                    },
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please choose a number.',
                    },
                  },
                },
              },
              {
                questionId: 'vehicle-alarm',
                questionComponent: 'question-vehicle-alarm',
                params: {
                  subtitle: 'Is the car fitted with an alarm/immobiliser?',
                  helpText: 'You can find this information in your vehicle handbook. If you have a Thatcham approved security device fitted, many insurance providers may require a certificate of installation.',
                  customFields: {
                    field: 'vehicleAlarm',
                  },
                },
              },
              {
                questionId: 'vehicle-tracking-device',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'vehicleTrackingDevice',
                  },
                  subtitle: 'Does it have a tracking device?',
                  validationRules: {
                    field: {
                      required: false,
                    },
                  },
                  defaultValues: {
                    field: false,
                  },
                },
              },
              {
                questionId: 'vehicle-grey-or-import',
                questionComponent: 'question-vehicle-import',
                params: {
                  customFields: {
                    field: 'vehicleGreyOrImport',
                  },
                  title: 'Is the car an import?',
                },
              },
            ],
          },
        },
        {
          questionComponent: 'question-estimated-value',
          questionId: 'estimated-value',
          params: {
            renderAfter: ['vehicleConfirm'],
            title: 'Roughly, how much is the car worth?',
            inputPrefix: '£',
            placeholder: 'e.g. 5000',
            maxLength: 7,
            helpText: this.estimatedValueHelpText,
            customFields: {
              vehicleLookupPrice: 'vehicleLookupPrice',
              estimatedValue: 'estimatedValue',
            },
            validationRules: {
              estimatedValue: {
                required: true,
                integer: true,
                between: [0, 9999999],
                minValue: 50,
              },
            },
            validationErrors: {
              estimatedValue: {
                required: 'Please enter the estimated value of your vehicle.',
                integer: 'Please only enter numbers e.g. £5000.',
                between: 'The estimated value of your vehicle must be between £50 and £9999999.',
                minValue: 'The estimated value of your vehicle must be greater than £50.',
              },
            },
          },
        },
        {
          questionComponent: 'question-modifications',
          questionId: 'modifications',
          params: {
            renderAfter: ['vehicleConfirm'],
            defaultValues: {
              vehicleModifiedFromManufacturer: false,
            },
            helpText: {
              value: `
                <p>
                  Modifications are non-standard changes made to the car after manufacture, including new spoilers, alloy wheels etc.
                </p>
                <p>
                  You can add as many modifications as you like. Some insurers may offer a discount for particular modifications. You don't need to include any Optional Extras that were added by the manufacturer when the vehicle was built.
                </p>
                <p>
                  If you have not declared a modification and you make a claim, then your claim may be refused and your insurance will be void.
                </p>
              `,
              collapsible: true,
              openerText: this.isExperimentMinimiseHelptext ? 'Understanding Vehicle Modifications' : 'Tell me more about the vehicle modifications',
            },
          },
        },
        {
          questionComponent: 'question-vehicle-purchase',
          questionId: 'vehicle-purchase',
          params: {
            renderAfter: ['vehicleConfirm'],
          },
        },
        {
          questionComponent: 'question-owned-registered',
          questionId: 'owner-registered',
          params: {
            renderAfter: ['vehicleConfirm'],
            defaultValues: {
              ownerOfVehicle: 1,
              registeredOfVehicle: 1,
            },
            helpText: this.registeredKeeperHelpText,
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'dashcam-fitted',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'dashcamFitted',
            },
            title: 'Do you have a dashcam fitted to your car?',
            helpText: this.dashcamHelpText,
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'vehicle-kept',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'vehicleKept',
            },
            title: 'Where do you keep your car at night?',
            helpText: 'Please select where it is kept <strong><u>overnight</u></strong> the majority of the time.',
            optionsConfig: {
              otherPlaceholder: 'Please select the location',
              mainItems: ['R', 'D', 'G', 'P'],
              options: {
                R: 'On the Road',
                D: 'On a Private Driveway',
                G: 'In a Locked Garage',
                U: 'In an Unlocked Garage',
                P: 'Residential Parking',
                N: 'Non-Secure Car Park',
                S: 'Secure Car Park',
                C: 'Carport',
                RH: 'Road Away From Home',
                RP: 'Railway Car Park',
                W: 'Work Car Park',
                F: 'Forces Base Car Park',
                L: 'Locked Compound',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'vehicle-kept-day',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'vehicleKeptDay',
            },
            title: 'Where do you keep the car during the day?',
            helpText: 'Please select where it is kept <strong><u>during the day</u></strong> the majority of the time.',
            optionsConfig: {
              otherPlaceholder: 'Please select the location',
              mainItems: ['H', 'PD', 'R', 'W'],
              options: {
                PD: 'On a Private Driveway',
                R: 'On the Road',
                H: 'At Home',
                W: 'Work Place Car Park',
                LG: 'In a Locked Garage',
                RP: 'Residential Parking',
                S: 'Secure Public Car Park',
                SH: 'On the Road Away From Home',
                C: 'Carport',
                G: 'In a Garage',
                O: 'Open Public Car Park',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'type-of-use',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'typeOfUse',
            },
            title: 'What will you use the car for?',
            helpText: {
              value: `
                <p>
                  <b>Social & Commuting</b> - day to day driving, including to and from a permanent place of work. This may not include any journeys that you might make while at work, such as driving to a client's office.
                </p>
                <p>
                  <b>Social only</b> - Day to day driving, excluding to and from a place of work.
                </p>
                <p>
                  <b>Social, Commuting & For Business</b> - day to day driving, including to and from a permanent place of work. It will also include any journeys that you might make while at work, such as driving to a client's office.
                </p>
                <p>
                  Your employer will usually ask you to have Business Use if it is needed for your job.
                </p>
              `,
              collapsible: true,
              openerText: 'Tell me more about these options',
            },
            optionsConfig: {
              mainItems: [4, 5],
              options: {
                4: 'Social & Commuting',
                5: 'Social Only',
                1: 'Business Use (by The Proposer)',
                6: 'Business Use by You and Spouse/Civil Partner',
                7: 'Business Use for All Drivers',
                8: 'Business Use by Spouse/Civil Partner',
                9: 'Commercial Travelling',
              },
              otherLabel: 'Social, Commuting & For Business',
              otherPlaceholder: 'Please select type of use',
              otherError: 'Please also select the type of business use from this list.',
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose a type.',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-number',
          questionId: 'annual-mileage',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'annualMileage',
            },
            title: 'What is your annual mileage?',
            subtitle: 'Including any business mileage',
            helpText: {
              value: 'It\'s easiest to think about how many miles you drive per week and multiply this up over the year (which should also include longer journeys for weekend trips etc). If other drivers will have access to the vehicle, then include any miles that they might make.',
              collapsible: this.isExperimentMinimiseHelptext,
              openerText: 'How to estimate annual mileage',
            },
            inputSuffix: 'miles',
            placeholder: 'e.g. 5000',
            maxLength: 6,
            validationRules: {
              field: {
                required: true,
                between: [1, 999999],
              },
            },
            validationErrors: {
              field: {
                required: 'Please enter your mileage e.g. 5000.',
                between: 'Please enter a numeric value between 1 and 999999.',
              },
            },
          },
        },
        {
          questionComponent: 'question-business-mileage',
          questionId: 'business-mileage',
          params: {
            renderAfter: ['vehicleConfirm'],
            showRules: {
              typeOfUse: {
                isAny: {
                  other: [1, 6, 7, 8, 9],
                },
                required: true,
              },
            },
            defaultValues: {
              businessMileage: null,
            },
          },
        },
      ];
    },
  },
  methods: {
    ...mapActions(['showToast']),

    setStepSharedData ({ estimatedValue }) {
      // no need to prefill estimated value if there's no such field yet at all
      if (!Object.prototype.hasOwnProperty.call(this.stepData, 'estimatedValue')) return;

      this.stepSharedData = { ...this.stepSharedData, estimatedValue };
    },
  },
  setup () {
    const {
      isCarHelpTextR1visible,
    } = inject('abTastyControllerUI');

    // COMPOSABLES
    const { isExperimentMinimiseHelptext } = useExperimentMinimiseHelptext();

    return {
      isCarHelpTextR1visible,
      isExperimentMinimiseHelptext,
    };
  },
};
</script>
